import { Radio } from "antd";
import { Form, Formik } from "formik";
import React, { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as Yup from "yup";
import { parseDate } from "../../../../utilities/filters";
import Button from "../../../Button";
import CustomDatePicker from "../../../CustomDatePicker";
import Overlay from "../../../Overlay";
import HeaderOverlay from "../../../Project/Header";
import Title from "../../../Project/Title";
import "./index.scss";
import { closeModal, openModal } from "../../../../data/slices/modals";

function ExportWritingList({ open, onCancel, data, ...rest }) {
  const dispatch = useDispatch();
  const ExportSchema = Yup.object().shape({
    exportType: Yup.string().required("Veuillez sélectionner un type d'export"),
    resultStartDate: Yup.date().when("exportType", {
      is: "result_period",
      then: (schema) =>
        schema.required("Veuillez sélectionner une date de début"),
      otherwise: (schema) => schema.notRequired(),
    }),
    resultEndDate: Yup.date().when("exportType", {
      is: "result_period",
      then: (schema) =>
        schema
          .required("Veuillez sélectionner une date de fin")
          .test(
            "date-order",
            "La date de fin doit être postérieure à la date de début",
            function (value) {
              const { resultStartDate } = this.parent;
              if (!resultStartDate || !value) return true;
              return new Date(value) > new Date(resultStartDate);
            }
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
    treasuryStartDate: Yup.date().when("exportType", {
      is: "treasury_period",
      then: (schema) =>
        schema.required("Veuillez sélectionner une date de début"),
      otherwise: (schema) => schema.notRequired(),
    }),
    treasuryEndDate: Yup.date().when("exportType", {
      is: "treasury_period",
      then: (schema) =>
        schema
          .required("Veuillez sélectionner une date de fin")
          .test(
            "date-order",
            "La date de fin doit être postérieure à la date de début",
            function (value) {
              const { treasuryStartDate } = this.parent;
              if (!treasuryStartDate || !value) return true;
              return new Date(value) > new Date(treasuryStartDate);
            }
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const { selectedMonths } = useSelector((state) => state.layout);

  const getStartAndEndDateSideBar = () => {
    const start = selectedMonths?.map((month) => parseDate(month))[0];
    const end = selectedMonths?.map((month) => parseDate(month))[
      selectedMonths?.length - 1
    ];

    return { start, end };
  };

  const { start, end } = getStartAndEndDateSideBar();

  const initialValues = useMemo(
    () => ({
      exportType: "all",
      resultStartDate: start,
      resultEndDate: end,
      treasuryStartDate: start,
      treasuryEndDate: end,
    }),
    [start, end]
  );

  const handleFormSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);

    try {
      let mode, startDate, endDate;

      const formatDate = (date) => {
        if (!date) return null;
        let d = date;
        if (typeof d === "string") {
          d = new Date(d);
        }
        if (d && typeof d.format === "function") {
          return d.format("YYYY-MM-DD");
        }
        if (d instanceof Date && !isNaN(d)) {
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`;
        }
        try {
          const parsed = new Date(d);
          if (!isNaN(parsed)) {
            const year = parsed.getFullYear();
            const month = String(parsed.getMonth() + 1).padStart(2, "0");
            const day = String(parsed.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
          }
        } catch (e) {}
        return null;
      };

      if (values.exportType === "all") {
        mode = "all";
        startDate = null;
        endDate = null;
      } else if (values.exportType === "result_period") {
        mode = "compta";
        startDate = formatDate(values.resultStartDate);
        endDate = formatDate(values.resultEndDate);
      } else if (values.exportType === "treasury_period") {
        mode = "treso";
        startDate = formatDate(values.treasuryStartDate);
        endDate = formatDate(values.treasuryEndDate);
      }

      if (startDate && endDate && new Date(startDate) >= new Date(endDate)) {
        console.error(
          "Date validation error: startDate must be before endDate"
        );
        return;
      }

      const exportData = {
        mode,
        startDate,
        endDate,
      };

      resetForm({ values: initialValues });

      dispatch(
        openModal("export-writing-filter-modal", {
          ...data,
          tableType: data?.tableType || "all",
          exportData,
        })
      );
      dispatch(closeModal("export-writing-list-modal"));
    } catch (error) {
      console.error("Export error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = (resetForm) => {
    if (resetForm) {
      resetForm({ values: initialValues });
    }

    if (typeof onCancel === "function") {
      onCancel();
    }
  };

  return (
    <Overlay show={open} closable={true}>
      <HeaderOverlay showlogo>
        <Title isDelete subTitle={`Export d'une liste des écritures`} />

        <Formik
          initialValues={initialValues}
          validationSchema={ExportSchema}
          onSubmit={handleFormSubmit}
          validateOnChange={true}
          validateOnBlur={true}
          enableReinitialize={true}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            setFieldValue,
            handleSubmit,
            resetForm,
          }) => {
            return (
              <Form
                className="export-writing-list-form"
                onSubmit={handleSubmit}
              >
                <div className="export-writing-list-content">
                  <div className="export-writing-list-content-wrapper">
                    <div className="export-writing-list-section">
                      <div className="section-wrapper-header">
                        <div className="section-title">
                          Vous souhaitez exporter:
                        </div>
                      </div>
                      <div className="section-content">
                        <div className="radio-options-container">
                          <Radio.Group
                            onChange={(e) => {
                              setFieldValue("exportType", e.target.value);
                              // Reset dates when switching types
                              if (e.target.value !== "result_period") {
                                setFieldValue("resultStartDate", start);
                                setFieldValue("resultEndDate", end);
                              }
                              if (e.target.value !== "treasury_period") {
                                setFieldValue("treasuryStartDate", start);
                                setFieldValue("treasuryEndDate", end);
                              }
                            }}
                            value={values.exportType}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 20,
                            }}
                          >
                            <div className="radio-option-item">
                              <Radio value="all">
                                Toutes les écritures de la base de données
                              </Radio>
                            </div>

                            <div className="radio-option-item">
                              <Radio value="result_period">
                                Les écritures appartenant à une période définie
                                de résultat:
                              </Radio>

                              <div
                                className={`date-range-section ${
                                  values.exportType !== "result_period"
                                    ? "disabled"
                                    : ""
                                }`}
                              >
                                <div className="date-picker-container">
                                  <div className="date-picker-item">
                                    <div className="date-label">Du:</div>
                                    <CustomDatePicker
                                      label=""
                                      name="resultStartDate"
                                      values={values.resultStartDate}
                                      setFieldValue={setFieldValue}
                                      errors={errors}
                                      touched={touched}
                                      disabled={
                                        values.exportType !== "result_period"
                                      }
                                      className="export-date-picker"
                                    />
                                  </div>
                                  <div className="date-picker-item">
                                    <div className="date-label">Au:</div>
                                    <CustomDatePicker
                                      label=""
                                      name="resultEndDate"
                                      values={values.resultEndDate}
                                      setFieldValue={setFieldValue}
                                      errors={errors}
                                      touched={touched}
                                      disabled={
                                        values.exportType !== "result_period"
                                      }
                                      className="export-date-picker"
                                    />
                                  </div>
                                </div>
                                {errors.resultStartDate &&
                                  touched.resultStartDate && (
                                    <div className="error-message">
                                      {errors.resultStartDate}
                                    </div>
                                  )}
                                {errors.resultEndDate &&
                                  touched.resultEndDate && (
                                    <div className="error-message">
                                      {errors.resultEndDate}
                                    </div>
                                  )}
                              </div>
                            </div>

                            <div className="radio-option-item">
                              <Radio value="treasury_period">
                                Les écritures appartenant à une période définie
                                de trésorerie:
                              </Radio>

                              <div
                                className={`date-range-section ${
                                  values.exportType !== "treasury_period"
                                    ? "disabled"
                                    : ""
                                }`}
                              >
                                <div className="date-picker-container">
                                  <div className="date-picker-item">
                                    <div className="date-label">Du:</div>
                                    <CustomDatePicker
                                      label=""
                                      name="treasuryStartDate"
                                      values={values.treasuryStartDate}
                                      setFieldValue={setFieldValue}
                                      errors={errors}
                                      touched={touched}
                                      disabled={
                                        values.exportType !== "treasury_period"
                                      }
                                      className="export-date-picker"
                                    />
                                  </div>
                                  <div className="date-picker-item">
                                    <div className="date-label">Au:</div>
                                    <CustomDatePicker
                                      label=""
                                      name="treasuryEndDate"
                                      values={values.treasuryEndDate}
                                      setFieldValue={setFieldValue}
                                      errors={errors}
                                      touched={touched}
                                      disabled={
                                        values.exportType !== "treasury_period"
                                      }
                                      className="export-date-picker"
                                    />
                                  </div>
                                </div>
                                {errors.treasuryStartDate &&
                                  touched.treasuryStartDate && (
                                    <div className="error-message">
                                      {errors.treasuryStartDate}
                                    </div>
                                  )}
                                {errors.treasuryEndDate &&
                                  touched.treasuryEndDate && (
                                    <div className="error-message">
                                      {errors.treasuryEndDate}
                                    </div>
                                  )}
                              </div>
                            </div>
                          </Radio.Group>

                          {errors.exportType && touched.exportType && (
                            <div className="error-message">
                              {errors.exportType}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="verify-inputs-popup">
                    <Button
                      text={"Annuler"}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleCancel(resetForm);
                      }}
                      className={"action-button"}
                      type="button"
                      disabled={isSubmitting}
                    />
                    <Button
                      text={"Continuer"}
                      htmlType="submit"
                      className={"action-button agree-action-button"}
                      disabled={isSubmitting}
                      loading={isSubmitting}
                    />
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </HeaderOverlay>
    </Overlay>
  );
}

export default ExportWritingList;
